/**
 * Site Context Store — Zustand replacement for AngularJS siteContextService
 *
 * Manages which navigation context is active (property, admin, vendorManagement, etc.).
 * The sidebar and subnav use this to show/hide context-specific menu items.
 *
 * In embedded mode, also calls the legacy AngularJS siteContextService.setContext()
 * so that the AngularJS sidebar stays in sync.
 *
 * Available contexts (from AngularJS siteContextService):
 *   property, workorder, audit, auditresults, CTD, overallowables,
 *   vendorManagement, companyDetails, companyMaintenance, admin, auditadmin,
 *   categories, components, servicearea, catsubcat, pricing, clientpricing,
 *   clientgrouping, scheduleoptions, allowable, caseManagementManager,
 *   none (''), hidden
 */

import { create } from 'zustand';

// ── Valid Contexts ──────────────────────────────────────────────────────────

export const AVAILABLE_CONTEXTS: Record<string, string> = {
  property: 'property',
  workOrder: 'workorder',
  audit: 'audit',
  auditResults: 'auditresults',
  overallowables: 'overallowables',
  vendorManagement: 'vendorManagement',
  companyDetails: 'companyDetails',
  companyMaintenance: 'companyMaintenance',
  admin: 'admin',
  auditadmin: 'auditadmin',
  categories: 'categories',
  components: 'components',
  serviceArea: 'serviceArea',
  catsubcat: 'catsubcat',
  pricing: 'pricing',
  clientpricing: 'clientpricing',
  clientgrouping: 'clientgrouping',
  scheduleoptions: 'scheduleoptions',
  allowable: 'allowable',
  caseManagementManager: 'caseManagementManager',
  none: '',
  hidden: 'hidden',
};

const validContextValues = new Set(Object.values(AVAILABLE_CONTEXTS));

// ── Store ───────────────────────────────────────────────────────────────────

interface SiteContextStore {
  currentContext: string;
  setContext: (context: string) => void;
}

export const useSiteContextStore = create<SiteContextStore>((set, get) => ({
  currentContext: '',

  setContext: (newContext: string) => {
    // Resolve camelCase key → value (e.g. 'workOrder' → 'workorder')
    const resolved = AVAILABLE_CONTEXTS[newContext] ?? newContext;

    // Don't update if context hasn't changed
    if (resolved === get().currentContext) return;

    // Validate
    if (!validContextValues.has(resolved)) {
      console.error(`"${newContext}" is not a valid site context`);
      return;
    }

    set({ currentContext: resolved });
  },
}));
